import { ProfitLossReportSummarySections } from "@common/dto/reports/reports-common.dto";
import {
    ProfitAndLossResponse,
    ProfitAndLossSection,
} from "@common/types/reports/types";
import { profitLossSectionLabels } from "@common/types/profitLossCoa";
import { ProfitLossSectionsOrder } from "./constants";
import { ProfitLossViewRow } from "./profitLossViewTypes";

type ProfitLossRowShape = NonNullable<ProfitAndLossSection["rows"]>[number];

function flattenAccountCodesFromRows(rows: ProfitLossRowShape[]): number[] {
    const codes: number[] = [];
    for (const row of rows) {
        if (row.kind !== "counterparty" && row.meta?.code !== undefined) {
            codes.push(row.meta.code);
        }
        if (row.children?.length) {
            codes.push(...flattenAccountCodesFromRows(row.children));
        }
    }
    return codes;
}

function getAccountCodesFromSection(
    report: ProfitAndLossResponse,
    sectionData?: ProfitAndLossSection,
): number[] {
    if (!sectionData) {
        return [];
    }

    if (sectionData.rows) {
        return flattenAccountCodesFromRows(sectionData.rows);
    }

    if (sectionData.meta?.summaryOfSections) {
        return sectionData.meta.summaryOfSections.flatMap((id) =>
            getAccountCodesFromSection(report, report.sections[id]),
        );
    }

    return [];
}

interface BuildSubtotalRowParams {
    id: string;
    category: string;
    values: ProfitLossViewRow["values"];
    accountCodes: ProfitLossViewRow["accountCodes"];
}

function buildSubtotalRow({
    id,
    category,
    values,
    accountCodes,
}: BuildSubtotalRowParams): ProfitLossViewRow {
    return {
        id,
        title: `Total ${category}`,
        values,
        isTotal: true,
        isSummary: false,
        isExpandable: false,
        accountCodes,
    };
}

interface BuildRowParams {
    sectionId: string;
    row: ProfitLossRowShape;
    path: string;
}

function buildRow({ sectionId, row, path }: BuildRowParams): ProfitLossViewRow {
    if (row.kind === "group") {
        const children = (row.children ?? []).map((child, index) =>
            buildRow({
                sectionId,
                row: child,
                path: `${path}:group:${index}`,
            }),
        );
        const groupAccountCodes = row.children?.length
            ? flattenAccountCodesFromRows(row.children)
            : undefined;
        if (children.length > 0) {
            children.push(
                buildSubtotalRow({
                    id: `total:group:${path}:${row.category}`,
                    category: row.category,
                    values: row.data,
                    accountCodes: groupAccountCodes,
                }),
            );
        }
        return {
            id: `group:${path}:${row.category}`,
            title: row.category,
            values: row.data,
            isTotal: false,
            isSummary: false,
            isExpandable: children.length > 0,
            accountCodes: groupAccountCodes,
            children: children.length > 0 ? children : undefined,
        };
    }

    if (row.kind === "counterparty") {
        return {
            id: `cp:${path}:${row.counterpartyId ?? "none"}`,
            title: row.category,
            values: row.data,
            isTotal: false,
            isSummary: false,
            isExpandable: false,
            counterpartyId: row.counterpartyId ?? null,
            counterpartyName: row.counterpartyName ?? null,
            accountCodes:
                row.meta?.code !== undefined ? [row.meta.code] : undefined,
        };
    }

    const counterpartyChildren = (row.children ?? [])
        .filter((child) => (child.kind ?? "counterparty") === "counterparty")
        .map((child, index) =>
            buildRow({
                sectionId,
                row: child,
                path: `${path}:cp:${index}`,
            }),
        );
    const parentAccountCode = row.meta?.code;
    const accountCodes =
        parentAccountCode !== undefined ? [parentAccountCode] : undefined;
    if (counterpartyChildren.length > 0) {
        counterpartyChildren.push(
            buildSubtotalRow({
                id: `total:row:${path}:${row.category}`,
                category: row.category,
                values: row.data,
                accountCodes,
            }),
        );
    }
    return {
        id: `row:${path}:${row.category}`,
        title: row.category,
        values: row.data,
        isTotal: false,
        isSummary: false,
        isExpandable: counterpartyChildren.length > 0,
        accountCodes,
        children:
            counterpartyChildren.length > 0 ? counterpartyChildren : undefined,
    };
}

export function buildProfitLossRows(
    report: ProfitAndLossResponse,
): ProfitLossViewRow[] {
    const result: ProfitLossViewRow[] = [];

    for (const sectionId of ProfitLossSectionsOrder) {
        const sectionData = report.sections[sectionId];
        if (!sectionData) {
            continue;
        }

        const isSummary = ProfitLossReportSummarySections.includes(sectionId);
        const accountCodes = getAccountCodesFromSection(report, sectionData);
        const hasRows = Boolean(sectionData.rows?.length);

        const children: ProfitLossViewRow[] = [];

        if (hasRows && sectionData.rows) {
            sectionData.rows.forEach((row, index) => {
                children.push(
                    buildRow({
                        sectionId,
                        row,
                        path: `${sectionId}:${index}`,
                    }),
                );
            });

            children.push(
                buildSubtotalRow({
                    id: `total:${sectionId}`,
                    category: profitLossSectionLabels[sectionId],
                    values: sectionData.data,
                    accountCodes,
                }),
            );
        }

        result.push({
            id: `section:${sectionId}`,
            title: profitLossSectionLabels[sectionId],
            values: sectionData.data,
            isTotal: false,
            isSummary,
            isExpandable: hasRows,
            accountCodes,
            children: children.length > 0 ? children : undefined,
        });
    }

    return result;
}

export function getProfitLossDefaultExpandedState(
    rows: ProfitLossViewRow[],
): Record<string, boolean> {
    const state: Record<string, boolean> = {};
    function visit(items: ProfitLossViewRow[]): void {
        for (const row of items) {
            if (row.isExpandable) {
                const hasCounterpartyChildren = row.children?.some(
                    (child) => child.counterpartyId !== undefined,
                );
                if (!hasCounterpartyChildren) {
                    state[row.id] = true;
                }
            }
            if (row.children?.length) {
                visit(row.children);
            }
        }
    }
    visit(rows);
    return state;
}
